import React, { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Plus, Trash2, Edit, X, Search, Filter, Archive, Link as LinkIcon, Download, Upload, FileText, ArrowUpDown } from 'lucide-react'
import Papa from 'papaparse'
import { ANNEX_A } from './Controls.jsx'
import { ConfirmModal } from '../components/ConfirmModal'
import MultiUserSelect from '../components/MultiUserSelect'

const CONTROL_TYPES = ['Preventive', 'Detective', 'Corrective']
const CATEGORIES = ['Administrative', 'Technical', 'Physical']
const STATUSES = ['Not Started', 'In Progress', 'Implemented', 'Planned', 'Not Applicable']
const DEPARTMENTS = ['IT & Security', 'HR', 'Admin', 'Sales', 'Finance', 'Marketing', 'Engineering', 'Product', 'Staffing/TA', 'GRC', 'Delivery Services']

// Flatten Annex A for the dropdown
const ALL_ANNEX_CONTROLS = ANNEX_A.flatMap(domain => 
  domain.controls.map(c => ({ id: c.id, label: `${c.id} - ${c.name}` }))
)

const mapPrefixToDept = (id) => {
  if (!id) return ''
  const prefix = id.split('-')[1]?.toUpperCase()
  if (!prefix) return ''
  const mapping = {
    'ADM': 'Admin',
    'HR': 'HR',
    'SALES': 'Sales',
    'FIN': 'Finance',
    'MKT': 'Marketing',
    'ENG': 'Engineering',
    'PROD': 'Product',
    'STAFF': 'Staffing/TA',
    'TA': 'Staffing/TA',
    'IT': 'IT & Security',
    'SEC': 'IT & Security',
    'IAM': 'IT & Security',
    'NET': 'IT & Security',
    'BCP': 'IT & Security',
    'END': 'IT & Security',
    'GRC': 'GRC',
    'COMP': 'GRC',
    'DEL': 'Delivery Services',
    'DS': 'Delivery Services'
  }
  return mapping[prefix] || ''
}

// Normalizes any date string to YYYY-MM-DD for HTML5 date inputs
function normalizeDate(val) {
  if (!val || typeof val !== 'string') return ''
  const v = val.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v              // already YYYY-MM-DD
  if (v.includes('T')) return v.split('T')[0]                // ISO datetime
  if (/^\d{4}-\d{2}-\d{2} /.test(v)) return v.split(' ')[0] // SQLite datetime with space
  const d = new Date(v)                                       // handles 'May 29, 2026' etc.
  if (!isNaN(d.getTime())) return d.toISOString().split('T')[0]
  return ''
}

function ControlModal({ control, onClose, onSave, isSaving, user }) {
  const [form, setForm] = useState(() => {
    if (control) return { 
      ...control, 
      owner_ids: control.owner_ids ? JSON.parse(control.owner_ids) : [], 
      annex_mapping: control.annex_control_id 
        ? control.annex_control_id.split(',').map(id => id.trim()).filter(Boolean) 
        : [],
      due_date: normalizeDate(control.due_date),
      assigned_date: normalizeDate(control.assigned_date)
    }
    return {
      control_id: '', name: '', description: '', 
      control_type: 'Preventive', control_category: 'Administrative', department: '',
      owner_ids: [], origin: 'Library', annex_mapping: [],
      implementation_status: 'Not Started', notes: '', due_date: '', assigned_date: ''
    }
  })

  // Auto-map department based on control_id prefix
  useEffect(() => {
    if (!control && form.control_id && !form.department) {
      const mappedDept = mapPrefixToDept(form.control_id)
      if (mappedDept) {
        setForm(f => ({ ...f, department: mappedDept }))
      }
    }
  }, [form.control_id, control, form.department])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 650 }}>
        <div className="modal-header">
          <h2>{control ? 'Edit Security Control' : 'Add New Security Control'}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Control ID *</label>
              <input className="form-input" disabled={!!control} value={form.control_id} onChange={e => set('control_id', e.target.value)} placeholder="e.g. CTRL-EDR-01" />
            </div>
            <div className="form-group">
              <label className="form-label">Control Name *</label>
              <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. CrowdStrike EDR" />
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">Annex A Mapping (Optional)</label>
            <select 
              className="form-select" 
              value="" 
              onChange={e => {
                const val = e.target.value
                if (!val) return
                setForm(f => {
                  const current = f.annex_mapping || []
                  const next = current.includes(val)
                    ? current.filter(id => id !== val)
                    : [...current, val]
                  return { ...f, annex_mapping: next }
                })
              }}
            >
              <option value="" disabled>Map Annex A controls...</option>
              {ALL_ANNEX_CONTROLS.map(c => (
                <option key={c.id} value={c.id}>
                  {form.annex_mapping?.includes(c.id) ? '✓ ' : ''} {c.label}
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
              {form.annex_mapping?.map(id => {
                const c = ALL_ANNEX_CONTROLS.find(ctrl => ctrl.id === id)
                return c ? (
                  <span key={id} className="badge badge-info" style={{ fontSize: '0.6875rem', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {c.label}
                    <X size={12} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => setForm(f => ({ ...f, annex_mapping: (f.annex_mapping || []).filter(currId => currId !== id) }))} />
                  </span>
                ) : null
              })}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" style={{ minHeight: 60 }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="What does this control do?" />
          </div>

          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" value={form.control_type} onChange={e => set('control_type', e.target.value)}>
                {CONTROL_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" value={form.control_category} onChange={e => set('control_category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Department</label>
              <select className="form-select" value={form.department || ''} onChange={e => set('department', e.target.value)}>
                <option value="">Select Department...</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Owner</label>
              <MultiUserSelect values={form.owner_ids || []} onChange={val => set('owner_ids', val)} disabled={user?.role === 'Contributor'} />
            </div>
            <div className="form-group">
              <label className="form-label">Implementation Status</label>
              <select className="form-select" value={form.implementation_status} onChange={e => set('implementation_status', e.target.value)}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input 
                type="date" 
                className="form-input" 
                value={form.due_date || ''} 
                onChange={e => set('due_date', e.target.value)} 
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Implementation Notes</label>
            <textarea className="form-textarea" style={{ minHeight: 80 }} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" 
            disabled={!form.control_id || !form.name || isSaving} 
            onClick={() => {
              const { annex_mapping, ...rest } = form
              onSave({
                ...rest,
                annex_control_id: annex_mapping
              })
            }}>
            {isSaving ? 'Saving…' : control ? 'Update Control' : 'Add to Library'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function ControlsLibrary({ toast, user }) {
  const qc = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  
  // Filtering and Sorting state
  const [search, setSearch] = useState('')
  const [filterDept, setFilterDept] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [sortField, setSortField] = useState('control_id')
  const [sortOrder, setSortOrder] = useState('asc')

  const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: api.getUsers });
  const { data: controls = [], isLoading } = useQuery({ queryKey: ['controls'], queryFn: api.getControls })

  const createMut = useMutation({
    mutationFn: api.createControl,
    onSuccess: () => { qc.invalidateQueries(['controls']); setShowModal(false); toast('Control added to library', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => api.updateControl(id, data),
    onSuccess: () => { qc.invalidateQueries(['controls']); setEditing(null); toast('Control updated', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const [confirmDelete, setConfirmDelete] = useState(null)
  const deleteMut = useMutation({
    mutationFn: ({ id, reason }) => api.deleteControl(id, reason),
    onSuccess: () => { qc.invalidateQueries(['controls']); toast('Control moved to bin', 'success') },
    onError: e => toast(e.message, 'error'),
  })
  
  const bulkCreateMut = useMutation({
    mutationFn: api.bulkCreateSecurityControls,
    onSuccess: (res) => { 
      qc.invalidateQueries(['controls'])
      toast(`${res.count} controls uploaded successfully`, 'success') 
    },
    onError: e => toast(e.message, 'error'),
  })

  const exportToCSV = () => {
    const data = filteredAndSorted.map(c => ({
      'Control ID': c.control_id,
      'Control Name': c.name,
      'Description': c.description,
      'Type': c.control_type,
      'Category': c.control_category,
      'Department': c.department || '',
      'Owner': c.owner_name || '',
      'Annex Mapping': c.annex_control_id || '',
      'Status': c.implementation_status,
      'Notes': c.notes
    }))
    const csv = Papa.unparse(data)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', `Security_Controls_Library_${new Date().toISOString().split('T')[0]}.csv`)
    link.click()
  }

  const downloadTemplate = () => {
    const template = [{
      'control_id': 'CTRL-EDR-01',
      'control_name': 'CrowdStrike EDR',
      'description': 'Endpoint detection and response for all laptops',
      'control_type': 'Preventive',
      'control_category': 'Technical',
      'department': 'IT & Security',
      'owner': 'IT Security',
      'annex_mapping': '8.5',
      'implementation_status': 'Implemented',
      'notes': 'Deployed via Intune'
    }]
    const csv = Papa.unparse(template)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', 'Controls_Bulk_Upload_Template.csv')
    link.click()
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data.map(row => ({
          ...row,
          origin: 'Library'
        }))
        bulkCreateMut.mutate(data)
      }
    })
    e.target.value = ''
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const isContributor = user?.role === 'Contributor' || user?.role === 'User'
  const allowedControls = isContributor ? (controls || []).filter(c => { try { return JSON.parse(c.owner_ids || '[]').includes(user?.id) } catch { return false } }) : (controls || [])

  const filteredAndSorted = useMemo(() => {
    let result = allowedControls.filter(c => {
      const matchSearch = (c?.name?.toLowerCase() || '').includes(search.toLowerCase()) || 
                          (c?.control_id?.toLowerCase() || '').includes(search.toLowerCase())
      const matchDept = filterDept ? c.department === filterDept : true
      const matchStatus = filterStatus ? c.implementation_status === filterStatus : true
      const matchType = filterType ? c.control_type === filterType : true
      const matchCategory = filterCategory ? c.control_category === filterCategory : true
      return matchSearch && matchDept && matchStatus && matchType && matchCategory
    })

    result.sort((a, b) => {
      let aVal = a[sortField] || ''
      let bVal = b[sortField] || ''
      
      // Specially handle status for sorting logically or alphabetically
      if (typeof aVal === 'string') aVal = aVal.toLowerCase()
      if (typeof bVal === 'string') bVal = bVal.toLowerCase()
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return result
  }, [allowedControls, search, filterDept, filterStatus, filterType, filterCategory, sortField, sortOrder])

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={12} style={{ opacity: 0.3, marginLeft: 4 }} />
    return <ArrowUpDown size={12} style={{ opacity: 0.8, marginLeft: 4, transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'none' }} />
  }

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Existing Controls Library</h1>
          <p>Centralized repository of all technical, administrative, and physical controls</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={downloadTemplate}>
            <Download size={15} /> Template
          </button>
          <button className="btn btn-secondary" onClick={exportToCSV}>
            <FileText size={15} /> Export CSV
          </button>
          <div style={{ position: 'relative' }}>
            <input 
              type="file" 
              accept=".csv" 
              onChange={handleFileUpload} 
              style={{ position: 'absolute', opacity: 0, inset: 0, cursor: 'pointer' }} 
            />
            <button className="btn btn-secondary">
              <Upload size={15} /> Bulk Upload
            </button>
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={15} /> Add Control
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="search-box" style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input 
            className="form-input" 
            style={{ paddingLeft: 34 }} 
            placeholder="Search by ID or name..." 
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="form-select" style={{ width: 160 }} value={filterDept} onChange={e => setFilterDept(e.target.value)}>
          <option value="">All Departments</option>
          {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className="form-select" style={{ width: 150 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="form-select" style={{ width: 150 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {CONTROL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="form-select" style={{ width: 150 }} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th onClick={() => handleSort('control_id')} style={{ cursor: 'pointer' }}>ID <SortIcon field="control_id" /></th>
              <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Control Name <SortIcon field="name" /></th>
              <th>Annex Mapping</th>
              <th>Type</th>
              <th>Category</th>
              <th onClick={() => handleSort('department')} style={{ cursor: 'pointer' }}>Department <SortIcon field="department" /></th>
              <th>Owner</th>
              <th onClick={() => handleSort('implementation_status')} style={{ cursor: 'pointer' }}>Status <SortIcon field="implementation_status" /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={9}><div className="empty-state">Loading Library...</div></td></tr>}
            {!isLoading && filteredAndSorted.length === 0 && <tr><td colSpan={9}><div className="empty-state">No security controls found. Adjust filters or add one.</div></td></tr>}
            {filteredAndSorted.map(c => (
              <tr key={c.id}>
                <td><span className="font-mono text-xs" style={{ color: 'var(--accent)', fontWeight: 700 }}>{c.control_id}</span></td>
                <td>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  <div className="text-xs text-muted truncate" style={{ maxWidth: 200 }}>{c.description || 'No description'}</div>
                </td>
                <td>
                  {c.annex_control_id ? (
                    <span className="badge badge-info" title="Mapped to Annex A">
                      <LinkIcon size={10} style={{ marginRight: 4 }} /> {c.annex_control_id}
                    </span>
                  ) : <span className="text-muted">—</span>}
                </td>
                <td><span className="badge badge-neutral">{c.control_type}</span></td>
                <td><span className="badge badge-neutral">{c.control_category}</span></td>
                <td style={{ fontSize: '0.8125rem' }}>{c.department || '—'}</td>
                <td style={{ fontSize: '0.8125rem' }}>{(() => {
                  try {
                    const ids = JSON.parse(c.owner_ids || '[]');
                    if (!ids.length) return '—';
                    return ids.map(id => users?.find(u => u.id === id)?.full_name || 'Unknown').join(', ');
                  } catch { return '—'; }
                })()}</td>
                <td>
                  <span className={`badge ${
                    c.implementation_status === 'Implemented' ? 'badge-success' : 
                    c.implementation_status === 'In Progress' ? 'badge-warning' : 'badge-danger'
                  }`}>
                    {c.implementation_status}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={() => setEditing(c)}><Edit size={14} /></button>
                    {user?.role !== 'Contributor' && user?.role !== 'User' && (
                      <button className="btn btn-ghost btn-sm" style={{ padding: 6, color: 'var(--danger)' }} onClick={() => setConfirmDelete(c)}><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && <ControlModal onClose={() => setShowModal(false)} onSave={d => createMut.mutate(d)} isSaving={createMut.isPending} user={user} />}
      {editing && <ControlModal control={editing} onClose={() => setEditing(null)} onSave={d => updateMut.mutate({ id: editing.id, data: d })} isSaving={updateMut.isPending} user={user} />}

      <ConfirmModal 
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={(reason) => deleteMut.mutate({ id: confirmDelete.id, reason })}
        title="Move to Bin?"
        message={`Delete control "${confirmDelete?.name}"? It will be moved to the Recycle Bin and can be restored later.`}
        showReason={true}
      />
    </div>
  )
}
